"use client";

import React, { useState } from "react";
import {
  Microscope,
  Cpu,
  Leaf,
  FlaskConical,
  Bot,
  Wifi,
  Printer,
  Award,
  FileText,
  Trophy,
  Lightbulb,
  Globe
} from "lucide-react";
import { cn } from "@/lib/utils";

import Image from "next/image";

export default function InnovationResearchSection() {
  const [activeTab, setActiveTab] = useState("focus");

  return (
    <div id="innovation-research" className="flex flex-col gap-8 sm:gap-12 py-10 sm:py-14 md:py-16 bg-white relative overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <Image
          src="/images/research-and-innovation/AI-Robotics.webp"
          alt="Innovation Lab Background"
          fill
          className="object-cover opacity-5"
        />
      </div>
      {/* HEADER SECTION */}
      <section className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <span className="text-accent font-semibold tracking-wide uppercase text-[11px] sm:text-sm mb-2 block">
            Cutting-Edge
          </span>
          <h2 className="text-[22px] sm:text-3xl lg:text-4xl font-bold text-[#100902] mb-4 sm:mb-6">
            Innovation & Research
          </h2>
          <p className="text-[12px] sm:text-sm md:text-base lg:text-lg text-gray-600 leading-relaxed font-light mb-6 sm:mb-8">
            Driving technological advancements and addressing global challenges through interdisciplinary research and state-of-the-art innovation labs.
          </p>

          {/* Tab Navigation */}
          <div className="flex flex-wrap justify-center gap-2">
            {[
              { id: "focus", label: "Research Focus", icon: Microscope },
              { id: "labs", label: "Innovation Labs", icon: FlaskConical },
              { id: "achievements", label: "Achievements", icon: Award },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "flex items-center gap-2 px-4 sm:px-6 h-10 sm:h-11 rounded-full text-[12px] sm:text-sm font-semibold transition-all duration-300 border",
                  activeTab === tab.id
                    ? "bg-[#100902] text-white border-[#100902] shadow-lg scale-105"
                    : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-white hover:border-gray-300"
                )}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* CONTENT SECTION */}
      <section className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto min-h-[320px] sm:min-h-[380px]">

          {/* RESEARCH FOCUS TAB */}
          {activeTab === "focus" && (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {[
                  {
                    icon: Bot,
                    title: "AI & Robotics",
                    desc: "Developing intelligent systems and autonomous robots.",
                    color: "text-blue-600",
                    bg: "bg-blue-50",
                    image: "/images/research-and-innovation/AI-Robotics.webp",
                  },
                  {
                    icon: Leaf,
                    title: "Sustainable Tech",
                    desc: "Green energy solutions and eco-friendly materials.",
                    color: "text-green-600",
                    bg: "bg-green-50",
                    image: "/images/research-and-innovation/SustainableTech.webp",
                  },
                  {
                    icon: Cpu,
                    title: "IoT & Embedded",
                    desc: "Smart devices and connected infrastructure.",
                    color: "text-purple-600",
                    bg: "bg-purple-50",
                    image: "/images/research-and-innovation/IoT-Embedded.webp",
                  },
                  {
                    icon: FlaskConical,
                    title: "Biotechnology",
                    desc: "Advanced research in genetics and healthcare.",
                    color: "text-red-600",
                    bg: "bg-red-50",
                    image: "/images/research-and-innovation/Biotechnology.webp",
                  },
                  {
                    icon: Globe,
                    title: "Data Science",
                    desc: "Big data analytics and predictive modeling.",
                    color: "text-cyan-600",
                    bg: "bg-cyan-50",
                    image: "/images/research-and-innovation/Data-Science.webp",
                  },
                  {
                    icon: Lightbulb,
                    title: "Entrepreneurship",
                    desc: "Business model innovation and market research.",
                    color: "text-yellow-600",
                    bg: "bg-yellow-50",
                    image: "/images/research-and-innovation/Entrepreneurship.webp",
                  },
                ].map((item, idx) => (
                  <div key={idx} className="group rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all duration-300 bg-white overflow-hidden">
                    <div className="relative h-44 sm:h-52 md:h-60">
                      <Image
                        src={item.image}
                        alt={item.title}
                        fill
                        className="object-cover transition-transform duration-700 group-hover:scale-105"
                        sizes="(max-width: 1024px) 100vw, 33vw"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/15 to-transparent" />
                      <div className="absolute left-3 bottom-3 sm:left-4 sm:bottom-4">
                        <div className={cn("w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center border border-white/20 bg-white/10 backdrop-blur-md shadow-sm", item.bg)}>
                          <item.icon className={cn("w-5 h-5 sm:w-6 sm:h-6", item.color)} />
                        </div>
                      </div>
                    </div>
                    <div className="p-4 sm:p-6">
                      <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-600 text-[12px] sm:text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* INNOVATION LABS TAB */}
          {activeTab === "labs" && (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-8">
                {[
                  {
                    icon: Wifi,
                    title: "IoT & Smart Systems Lab",
                    desc: "Equipped with sensors, microcontrollers, and communication modules for building smart city solutions.",
                    tags: ["Arduino", "Raspberry Pi", "LoRaWAN"],
                    image: "/images/research-and-innovation/IoT-Embedded.webp",
                  },
                  {
                    icon: Printer,
                    title: "Makerspace & 3D Printing",
                    desc: "A creative space for rapid prototyping using 3D printers, laser cutters, and CNC machines.",
                    tags: ["Prototyping", "CAD Design", "Fabrication"],
                    image: "/images/research-and-innovation/SustainableTech.webp",
                  },
                  {
                    icon: Cpu,
                    title: "Advanced Computing Lab",
                    desc: "High-performance computing clusters for simulation, modeling, and AI training.",
                    tags: ["HPC", "GPU Clusters", "Simulation"],
                    image: "/images/research-and-innovation/Data-Science.webp",
                  },
                  {
                    icon: Bot,
                    title: "Robotics & Automation",
                    desc: "Industrial arms, drones, and mobile robots for research in automation and control.",
                    tags: ["ROS", "Automation", "Drones"],
                    image: "/images/research-and-innovation/AI-Robotics.webp",
                  }
                ].map((lab, idx) => (
                  <div key={idx} className="rounded-2xl bg-gray-50 border border-gray-100 hover:bg-white hover:shadow-md transition-all overflow-hidden">
                    <div className="relative h-48 sm:h-60 md:h-80">
                      <Image
                        src={lab.image}
                        alt={lab.title}
                        fill
                        className="object-cover"
                        sizes="(max-width: 1024px) 100vw, 50vw"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/15 to-transparent" />
                      <div className="absolute left-3 bottom-3 sm:left-4 sm:bottom-4">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center text-white shadow-sm">
                          <lab.icon className="w-5 h-5 sm:w-6 sm:h-6" />
                        </div>
                      </div>
                    </div>
                    <div className="p-4 sm:p-6">
                      <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-2">{lab.title}</h3>
                      <p className="text-gray-600 text-[12px] sm:text-sm mb-3 sm:mb-4 leading-relaxed">{lab.desc}</p>
                      <div className="flex flex-wrap gap-2">
                        {lab.tags.map((tag, tIdx) => (
                          <span key={tIdx} className="px-3 py-1 bg-white border border-gray-200 rounded-full text-[11px] sm:text-xs font-medium text-gray-600">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ACHIEVEMENTS TAB */}
          {activeTab === "achievements" && (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
                {[
                  {
                    value: "8+",
                    label: "Patents Filed",
                    icon: FileText,
                    desc: "Innovation-driven ideas protected through intellectual property filings.",
                    image: "/images/research-and-innovation/Research_revie_in_a_collaborative_workspace.png",
                  },
                  {
                    value: "₹51 Lakhs",
                    label: "Research Grant",
                    icon: Trophy,
                    desc: "Research grant received from an industry body to support impactful projects.",
                    image: "/images/research-and-innovation/Mentoring_in_the_research_room.png",
                  },
                  {
                    value: "30+",
                    label: "Publications",
                    icon: Globe,
                    desc: "Research papers published in reputed journals and conference proceedings.",
                    image: "/images/research-and-innovation/Students_working_in_advanced_research_lab.png",
                  }
                ].map((stat, idx) => (
                  <div key={idx} className="rounded-2xl bg-gradient-to-br from-white to-gray-50 border border-gray-100 hover:shadow-lg transition-all overflow-hidden">
                    <div className="relative h-44 sm:h-52 md:h-60">
                      <Image
                        src={stat.image}
                        alt={stat.label}
                        fill
                        className="object-cover"
                        sizes="(max-width: 1024px) 100vw, 33vw"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                      <div className="absolute left-3 bottom-3 sm:left-4 sm:bottom-4">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center text-white shadow-sm">
                          <stat.icon className="w-5 h-5 sm:w-6 sm:h-6" />
                        </div>
                      </div>
                    </div>
                    <div className="text-center p-4 sm:p-6">
                      <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2">{stat.value}</div>
                      <div className="text-sm sm:text-base md:text-lg font-semibold text-gray-800 mb-2 sm:mb-3">{stat.label}</div>
                      <p className="text-[12px] sm:text-sm text-gray-500">{stat.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
